import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import FilterSelect from "../../components/forms/FilterSelect";
import Skeleton from "../../components/Skeleton/Skeleton";
import { formatMonthDay, parseLocalISODate } from "../../utils/helpers/dateDisplay";
import { META_LABEL } from "./coverageStyles";

// A period is a RANGE, and the range is the number's defence: "48 visits" means
// nothing until the strip beside it says over which eleven weeks. So the
// control shows every preset already resolved to real dates, and the selected
// one repeats them in full.
//
// The options come from the coverage response's own `periods` list, not from
// `getAcademicTerms`. AcademicTerm stores its boundaries as YEAR-LESS "MM-DD"
// strings that only become dates through the backend's PIVOT_MONTH rule — the
// rule that, set one month wrong, once resolved term 1 to 2026-08-10..2025-10-31
// and matched no date for 138 days a year. Re-deriving that in the browser would
// be a second copy of it, free to drift from the one the counts were computed
// with; the response already carries each preset resolved by the same code path
// that resolved the window it answered.
const nameOf = (preset, t) => {
  if (!preset) return "";
  if (preset.key.startsWith("term-")) return t("coverage.period.term", { name: preset.label });
  if (preset.key === "year") return preset.label || t("coverage.period.year");
  if (preset.key === "days30") return t("coverage.period.days30");
  if (preset.key === "days90") return t("coverage.period.days90");
  return t("coverage.period.custom");
};

// Date-only strings parse as UTC through `new Date`, which lands on the
// previous calendar day west of Greenwich — parseLocalISODate keeps the day.
const range = (preset) =>
  preset?.start && preset?.end
    ? `${formatMonthDay(parseLocalISODate(preset.start))} — ${formatMonthDay(parseLocalISODate(preset.end))}`
    : "";

export default function PeriodSelect({
  periods = [],
  period,
  value,
  onChange,
  // True when the client asked for nothing (or for a term) and the server
  // answered with the 30-day window instead, because no term covers today.
  fellBack = false,
  isLoading = false,
}) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      periods.map((preset) => ({
        value: preset.key,
        label: `${nameOf(preset, t)} · ${range(preset)}`,
      })),
    [periods, t],
  );

  // The selected key is the one the SERVER resolved, so a fallback or a
  // discarded garbage value shows the window actually measured rather than the
  // one that was asked for.
  const selected = period?.key || value;
  const pct =
    period?.total_days > 0
      ? Math.min(100, Math.round((period.elapsed_days / period.total_days) * 100))
      : 0;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className={META_LABEL}>{t("coverage.period.label")}</span>
        {isLoading && options.length === 0 ? (
          <Skeleton className="h-[38px] w-56 rounded-xl" />
        ) : (
          <FilterSelect
            options={options}
            value={selected}
            onChange={onChange}
            ariaLabel={t("coverage.period.label")}
            placeholder={t("coverage.period.label")}
            minWidthClass="min-w-[15rem]"
            searchable={options.length > 8}
            searchPlaceholder={t("coverage.period.label")}
            emptyText={t("coverage.empty")}
          />
        )}
      </div>

      {period && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          {!period.started ? (
            // Said at the control rather than as a banner further down: the
            // period is the thing that makes every count on the page zero, so
            // the explanation belongs next to the picker that chose it.
            <>
              <span className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-500">
                {t("coverage.period.notStarted")}
              </span>
              <span className="text-[11px] font-medium text-slate-400">
                {t("coverage.period.notStartedBody")}
              </span>
            </>
          ) : (
            <>
              {/* The bar is decoration for the sentence beside it, never the
                  carrier: a progress bar alone does not say 41 of 76. */}
              <span className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200" aria-hidden="true">
                <span className="block h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} />
              </span>
              <span className="text-[11px] font-bold text-slate-500 tabular-nums">
                {t("coverage.period.progress", {
                  elapsed: period.elapsed_days,
                  total: period.total_days,
                })}
              </span>
            </>
          )}
        </div>
      )}

      {fellBack && (
        <p className="max-w-md text-[11px] font-semibold leading-snug text-amber-700">
          {t("coverage.period.fallbackNotice")}
        </p>
      )}
    </div>
  );
}
